import classNames from 'classnames'
import { FC, forwardRef, lazy, Ref, Suspense, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import sanitize from 'sanitize-html'

import { IForm } from 'core-components/classes/models/IForm'
import { getContentWithoutTags } from 'core-components/helpers/getContentWithoutTags'
import { Button } from 'core-components/library/button'
import { IFileSettings } from 'core-components/library/dragNDropFileUploader'
import { Input } from 'core-components/library/inputs'
import { Loader } from 'core-components/library/loader'
import { ModalDialog } from 'core-components/library/modalDialog'
import {
  IToolbarButtonView,
  IToolbarCustomButtons,
  RichTextEditor,
} from 'core-components/library/richTextEditor'
import {
  Controller,
  IControllerResult,
  ValidateInput,
  ValidateRichTextEditor,
} from 'core-components/library/validation'
import { FlexDirectionBreak } from 'core-components/sitebuilder/models/flexGrid/FlexDirectionBreak'
import MeasureController from 'src/core/analytics/MeasureController'
import { IUploadAdapter } from 'src/core/classes/models/IUploadAdapter'
import { ActionBtn } from 'src/core/components/shared/ui/editableArea/Actions'
import { EditableAreaEventHandlerService } from 'src/core/components/shared/ui/editableArea/EditableAreaEventHandlerService'
import { usePrompt } from 'src/core/routes/usePrompt'

import { IProps as HtmlCodeEditorProps } from './editableArea/components/HtmlCodeEditor'

import styles from './EditableArea.module.scss'

export interface IEditableAreaProps {
  className?: string
  themeKbase?: boolean
  textStyle?: any // put in classes prop
  text: string
  emptyText?: string
  onChange?: (newValue: string) => Promise<unknown>
  onClose?: () => void
  multiline?: boolean
  fullRichEditorConfig?: boolean // only for multiline
  multilineOptions?: string[] // only for multiline
  enableSnippets?: boolean // only for multiline
  fileUploadAdapter?: ((file: IFileSettings) => Promise<string>) | IUploadAdapter // only for multiline
  fileDeleteAdapter?: (url: string) => void // only for multiline
  snippetsToolbarWidth?: number // only for multiline
  useBlockMode?: boolean // only for multiline
  useEditorMinHeight?: boolean // only for multiline
  disableViewModeChange?: boolean // only for multiline
  disabled?: boolean
  withoutPencil?: boolean
  onlyPencilEdit?: boolean
  showPencilOnHover?: boolean
  disableBorder?: boolean
  autoFocus?: boolean
  urlDeps?: string[]
  ignoreUrlDeps?: IForm
  widgetWidth?: number
  flexWidth?: FlexDirectionBreak
  metrics?: MeasureController
  checkFn?: ((value: string) => Promise<string>) | null
  onChangeValidation?: (result: IControllerResult) => void
  onChangeWithoutSave?: (value?: string) => void
  reservedValue?: string
  actionsInside?: boolean
  hideImageActions?: boolean
  viewModes?: { key: string; label: string }[]
  toolbarPosition?: 'left' | 'right'
  toolbarVisible?: boolean
  toolbarClassName?: string
  newRichTextEditorClassName?: string
  minCountOfLines?: 1 | 2 | 3 | 4 | 5
  placeholder?: string
  isCustomHtml?: boolean
  toolbarButtonsView?: Partial<IToolbarButtonView>
  toolbarCustomButtons?: IToolbarCustomButtons[]
}

export const defaultViewValues = [
  { key: 'view', label: 'CONTENT VIEW' },
  { key: 'html', label: 'HTML VIEW' },
  { key: 'source', label: 'SOURCE VIEW' },
]

const imageUrlWithoutCloudinary = (src: string) => {
  if (src && src.includes('https://res.cloudinary.com')) {
    const urls = src.split('https')
    if (urls.length > 2) {
      const cloudinaryUrl = 'https' + urls[1]
      const imageUrl = src.replace(cloudinaryUrl, '')
      return imageUrl
    }
  }

  return src
}

const HtmlCodeEditorComponent = lazy(() =>
  import('./editableArea/components/HtmlCodeEditor').then((m) => ({
    default: m.HtmlCodeEditor,
  }))
)

const LazyHtmlCodeEditorComponent: FC<HtmlCodeEditorProps> = (props: HtmlCodeEditorProps) => (
  <Suspense fallback={<Loader dimension={{ width: 24, height: 24 }} />}>
    <HtmlCodeEditorComponent {...props} />
  </Suspense>
)

const isTextChanged = (
  isEditing: boolean,
  oldText: string | null,
  newText: string | null,
  search: string
) => {
  const preparedText = 'Lorem ipsum dolor sit amet, consectetur adipiscing elit'
  const isDeleting = search.includes('remove=true')
  return (
    isEditing &&
    !isDeleting &&
    !(newText || '').includes(preparedText) &&
    getContentWithoutTags(newText || '') !== getContentWithoutTags(oldText || '')
  )
}

export const EditableArea = forwardRef(
  (
    {
      toolbarVisible,
      text,
      onChange,
      emptyText,
      multiline,
      disabled,
      className,
      withoutPencil,
      disableBorder,
      textStyle,
      showPencilOnHover,
      autoFocus,
      metrics,
      onClose,
      onlyPencilEdit,
      fileUploadAdapter,
      fileDeleteAdapter,
      useEditorMinHeight,
      checkFn,
      onChangeValidation,
      onChangeWithoutSave,
      reservedValue,
      disableViewModeChange,
      themeKbase,
      actionsInside,
      hideImageActions,
      viewModes,
      toolbarClassName,
      newRichTextEditorClassName,
      minCountOfLines = 5,
      placeholder,
      isCustomHtml,
      toolbarButtonsView,
      toolbarCustomButtons,
    }: IEditableAreaProps,
    ref: Ref<HTMLDivElement>
  ) => {
    const [viewValues] = useState(viewModes ? viewModes : defaultViewValues)
    const [isEditing, setIsEditing] = useState(false)
    const [textEditingValue, setTextEditingValue] = useState(text)
    const [mode, setMode] = useState(viewModes ? 0 : 1)
    const [fullScreenImageSrc, setFullScreenImageSrc] = useState('')
    const [valid, setValid] = useState(true)

    const timerRef = useRef<number | null>(null)
    const location = useLocation()
    const navigate = useNavigate()
    const showPrompt = isTextChanged(isEditing, text, textEditingValue, location.search)
    usePrompt('You have unsaved data, would you really like to go from the page?', showPrompt)
    const textRef = useRef(textEditingValue)
    textRef.current = textEditingValue

    const onEditingValueChange = (value: string) => {
      setTextEditingValue(() => value)
      onChangeWithoutSave && onChangeWithoutSave(value)
    }

    useEffect(() => {
      setTextEditingValue(text)
    }, [text])

    useEffect(() => {
      if (typeof autoFocus === 'boolean') {
        setIsEditing(autoFocus)
        onChangeWithoutSave && onChangeWithoutSave(text)
      }
    }, [autoFocus, text])

    useEffect(() => {
      timerRef.current = window.setTimeout(() => {
        if (reservedValue !== undefined && reservedValue !== textEditingValue) {
          onEditingValueChange(reservedValue)
        }
      }, 0)

      return () => clearTimeout(timerRef.current || -1)
    }, [reservedValue])

    const isButtonsDisabled = text === textEditingValue
    const doneFn = (e: any) => {
      if (!valid || isButtonsDisabled) {
        return
      }
      e.stopPropagation()
      setIsEditing(false)
      e.preventDefault()
      onChange && onChange(textEditingValue)
    }

    const closeFn = (e: any) => {
      e.stopPropagation()
      setIsEditing(false)
      onEditingValueChange(text)
      e.preventDefault()
      onClose && onClose()
    }

    return (
      <div
        onClick={(e) => {
          if (!disabled) {
            const el = e.target
            if (el && (el as HTMLElement).tagName === 'INPUT') {
              return
            }
            e.stopPropagation()
            let isSelectedText: boolean | null = false
            if (window && window.getSelection()) {
              const selected = window.getSelection()
              isSelectedText = selected && selected.toString().length > 0
            }
            !isEditing && !disabled && !isSelectedText && !onlyPencilEdit && setIsEditing(true)
          }
        }}
        className={classNames(
          styles.root,
          disabled || disableBorder ? styles['root--no-border'] : '',
          onlyPencilEdit ? styles['root--only-pencil-edit'] : '',
          isEditing && themeKbase && !multiline ? styles['root--editing-kbase'] : '',
          (isEditing && themeKbase && !multiline) ||
            (isEditing && multiline && disableViewModeChange)
            ? styles['root--side-actions']
            : '',
          isEditing ? styles['root--editing'] : '',
          actionsInside && !(isEditing && multiline) && !(isEditing && themeKbase)
            ? styles['root--actions-inside']
            : '',
          className || '',
          hideImageActions ? styles['hide_actions'] : ''
        )}
      >
        {isEditing ? (
          multiline ? (
            !disableViewModeChange ? (
              <>
                <header className={styles.multilineHeader}>
                  <div className={styles.multilineMods}>
                    {viewValues.map((item, index) => (
                      <Button
                        className={styles.multilineMods__item}
                        key={viewValues[index].key}
                        variant={index === mode ? 'contained' : 'outlined'}
                        onClick={() => setMode(index)}
                      >
                        {item.label}
                      </Button>
                    ))}
                  </div>
                  <div className={styles.multilineActions}>
                    {!valid || isButtonsDisabled ? (
                      <></>
                    ) : (
                      <ActionBtn
                        className={styles.multilineActions__item}
                        type={'done'}
                        onClick={doneFn}
                        themeKbase
                        disabled={!valid || isButtonsDisabled}
                      />
                    )}
                    <ActionBtn
                      className={styles.multilineActions__item}
                      type={'close'}
                      onClick={closeFn}
                      themeKbase
                    />
                  </div>
                </header>
                {viewValues[mode].key === 'html' && (
                  <div ref={ref} className={useEditorMinHeight ? 'min-height' : ''}>
                    <RichTextEditor
                      classes={{
                        root: newRichTextEditorClassName,
                        toolbar: toolbarClassName,
                      }}
                      minLinesCount={minCountOfLines}
                      onChange={(value) => {
                        onEditingValueChange(value)
                      }}
                      value={textEditingValue}
                      toolbarVisible={toolbarVisible}
                      onFileRemove={fileDeleteAdapter}
                      fileUploadAdapter={
                        fileUploadAdapter as (file: IFileSettings) => Promise<string>
                      }
                      placeholder={placeholder}
                      toolbarButtonsView={toolbarButtonsView}
                      toolbarCustomButtons={toolbarCustomButtons}
                    />
                  </div>
                )}
                {viewValues[mode].key === 'source' && (
                  <LazyHtmlCodeEditorComponent
                    value={textEditingValue}
                    onChange={(value: string) => onEditingValueChange(value)}
                  />
                )}
                {viewValues[mode].key === 'view' && (
                  <div className={useEditorMinHeight ? 'min-height' : ''}>
                    <div className={'ck-content'}>
                      <div
                        dangerouslySetInnerHTML={{
                          __html: sanitize(textEditingValue) || emptyText || '',
                        }}
                        ref={ref}
                      />
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                <div ref={ref} className={'min-height-md'}>
                  {checkFn ? (
                    <Controller
                      onValidationStatusChange={(result) => {
                        if (result.status === 'Valid') {
                          setValid(true)
                        } else if (result.status === 'Invalid') {
                          setValid(false)
                        }
                        onChangeValidation && onChangeValidation(result)
                      }}
                    >
                      <ValidateRichTextEditor
                        classes={{
                          validatorClasses: {
                            root: newRichTextEditorClassName,
                          },
                        }}
                        minLinesCount={minCountOfLines}
                        toolbarVisible={toolbarVisible}
                        checkFn={checkFn}
                        value={textEditingValue}
                        onChange={(value) => {
                          onEditingValueChange(value)
                        }}
                        onKeyDown={(e) => {
                          if (e.code === 'Enter' && e.ctrlKey) {
                            setIsEditing(false)
                            onChange && onChange(textEditingValue)
                            e.preventDefault()
                          }
                        }}
                        fileUploadAdapter={fileUploadAdapter}
                        onFileRemove={fileDeleteAdapter}
                        placeholder={placeholder}
                      />
                    </Controller>
                  ) : (
                    <RichTextEditor
                      classes={{
                        root: newRichTextEditorClassName,
                      }}
                      minLinesCount={minCountOfLines}
                      onChange={(value) => {
                        onEditingValueChange(value)
                      }}
                      value={textEditingValue}
                      toolbarVisible={toolbarVisible}
                      onFileRemove={fileDeleteAdapter}
                      fileUploadAdapter={
                        fileUploadAdapter as (file: IFileSettings) => Promise<string>
                      }
                      placeholder={placeholder}
                    />
                  )}
                </div>
                <div
                  className={classNames(
                    styles.actions,
                    themeKbase || (multiline && disableViewModeChange)
                      ? styles['actions--on-side']
                      : '',
                    actionsInside ? styles['actions--inside'] : ''
                  )}
                >
                  <ActionBtn
                    className={styles.actions__item}
                    type={'done'}
                    onClick={doneFn}
                    themeKbase
                    disabled={!valid || isButtonsDisabled}
                  />
                  <ActionBtn
                    className={styles.actions__item}
                    type={'close'}
                    onClick={closeFn}
                    themeKbase
                  />
                </div>
              </>
            )
          ) : (
            <>
              <div
                className={classNames(
                  styles.inputWrapper,
                  themeKbase ? styles['inputWrapper--kbase'] : '',
                  textStyle || ''
                )}
                onKeyDown={(e) => {
                  if (e.code === 'Enter') {
                    e.stopPropagation()
                    setIsEditing(false)
                    onChange && onChange(textEditingValue)
                  } else if (e.code === 'Escape') {
                    setIsEditing(false)
                    onEditingValueChange(text)
                  } else if (e.code === 'Space') {
                    e.stopPropagation()
                  }
                }}
              >
                {checkFn ? (
                  <Controller
                    onValidationStatusChange={(result) => {
                      if (result.status === 'Valid') {
                        setValid(true)
                      } else if (result.status === 'Invalid') {
                        setValid(false)
                      }
                      onChangeValidation && onChangeValidation(result)
                    }}
                  >
                    <ValidateInput
                      checkFn={checkFn}
                      value={textEditingValue}
                      onChange={(e) => onEditingValueChange(e)}
                      autoFocus
                    />
                  </Controller>
                ) : (
                  <Input
                    value={textEditingValue}
                    onChange={(e: string) => {
                      onEditingValueChange(e)
                    }}
                    autoFocus
                  />
                )}
              </div>
              <div
                className={classNames(
                  styles.actions,
                  themeKbase || (multiline && disableViewModeChange)
                    ? styles['actions--on-side']
                    : '',
                  actionsInside ? styles['actions--inside'] : ''
                )}
              >
                <ActionBtn
                  className={styles.actions__item}
                  type={'done'}
                  onClick={doneFn}
                  themeKbase={themeKbase}
                  disabled={!valid || isButtonsDisabled}
                />
                <ActionBtn
                  className={styles.actions__item}
                  type={'close'}
                  onClick={closeFn}
                  themeKbase={themeKbase}
                />
              </div>
            </>
          )
        ) : (
          <div
            className={styles.inner}
            onMouseUp={(e: any) => {
              e.stopPropagation()
              e.preventDefault()
            }}
          >
            {!(disabled || withoutPencil) && (
              <div
                className={classNames(
                  styles.actions,
                  showPencilOnHover ? styles['actions--show-on-hover'] : '',
                  actionsInside ? styles['actions--inside'] : ''
                )}
              >
                <ActionBtn
                  type={'edit'}
                  onClick={() => onlyPencilEdit && !isEditing && !disabled && setIsEditing(true)}
                />
              </div>
            )}
            {multiline ? (
              <div className={useEditorMinHeight ? 'min-height' : ''}>
                <div ref={ref}>
                  {!isCustomHtml ? (
                    <RichTextEditor
                      minLinesCount={minCountOfLines}
                      classes={{
                        root: newRichTextEditorClassName || '',
                        toolbar: toolbarClassName,
                      }}
                      onChange={(value) => {
                        onEditingValueChange(value)
                      }}
                      value={sanitize(textEditingValue, {
                        allowedTags: false,
                        allowedAttributes: false,
                        exclusiveFilter: (item) => item.tag === 'script',
                        parseStyleAttributes: false,
                      })}
                      onFileRemove={fileDeleteAdapter}
                      toolbarVisible={false}
                      disabled={true}
                    />
                  ) : (
                    <div
                      onClick={(e) =>
                        EditableAreaEventHandlerService.onClick(
                          e,
                          (src) => {
                            const newUrl = imageUrlWithoutCloudinary(src)
                            setFullScreenImageSrc(newUrl)
                          },
                          () => {
                            // This handler presented here if
                            // you wanted make additional action
                          },
                          navigate
                        )
                      }
                      className={styles.sectionFromNewEditor}
                      dangerouslySetInnerHTML={{
                        __html:
                          sanitize(text, {
                            allowedTags: false,
                            allowedAttributes: false,
                            exclusiveFilter: (item) => item.tag === 'script',
                            parseStyleAttributes: false,
                          }) ||
                          emptyText ||
                          '',
                      }}
                    />
                  )}
                </div>
              </div>
            ) : (
              <p className={classNames(styles.text, textStyle || '')}>
                {textEditingValue || (
                  <span className={styles.text__placeholder}>{emptyText || ''}</span>
                )}
              </p>
            )}
          </div>
        )}

        <ModalDialog
          size={'auto'}
          show={!!fullScreenImageSrc}
          content={() => <img className={styles.fullScreenImage} src={fullScreenImageSrc} alt="" />}
          onClose={() => setFullScreenImageSrc('')}
        />
      </div>
    )
  }
)

EditableArea.displayName = 'EditableArea'
